import React, { useMemo, useState } from 'react';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import { getDirectReports, OrgChartRow } from '../../../support/orgScope';
import { categorizeItems } from '../../../support/enablementLeader';
import {
  useEducationCompletionByEmail,
  type EducationDrillNode,
} from '../../../hooks/education-completion-hooks';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';
import EducationSeriesTable, { seriesRowFor } from './EducationSeriesTable';

/**
 * Leader > Enablement > Team, first section. One row per DIRECT report with how many required series they
 * have and how many are complete; opening a row lists that person's required series (progress, modules,
 * status, certification) exactly as the individual view does.
 *
 * Each report is its own card. The counts come from the manager's drill node and are always the person's OWN
 * required series, a sub-manager included (their team's figures are on the By Team Member table). The series
 * are fetched per person, only once that card is opened. The list scrolls so the section stays short.
 */

interface Props {
  orgRows: OrgChartRow[];
  managerEmail: string;
  /** The manager's education drill node (report stubs carry summaries only); null when unavailable. */
  eduNode: EducationDrillNode | null;
  /** Enablement time dropdown as a `time_filter`; the per-person fetch uses the same window. */
  timeFilter?: string;
}

interface ReportRow {
  email: string;
  isManager: boolean;
  required: number;
  completed: number;
}

// Tall enough for six collapsed cards, with a sliver of the seventh showing that the list scrolls.
const LIST_MAX_HEIGHT = 340;

const COUNT_COL = { width: 96, textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const };

function ReportRequiredSeries({ email, timeFilter }: { email: string; timeFilter?: string }) {
  const { data, isLoading, isError } = useEducationCompletionByEmail(email, { timeFilter });
  const rows = useMemo(
    () => categorizeItems(data?.node?.own?.items).required.map(seriesRowFor('Required')),
    [data],
  );

  if (isLoading) return <HbrSpinner>Loading required education…</HbrSpinner>;
  if (isError) {
    return <div style={{ color: 'red' }}>Required education could not be loaded for {email}.</div>;
  }
  return (
    <>
      {rows.length ? (
        <EducationSeriesTable rows={rows} />
      ) : (
        <div className="hbr-css__text-secondary-sm">No required education assigned.</div>
      )}
    </>
  );
}

export default function EnablementRequiredByReport({ orgRows, managerEmail, eduNode, timeFilter }: Props) {
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const rows = useMemo<ReportRow[]>(() => {
    const stubs = new Map((eduNode?.reports ?? []).map((r) => [String(r.email || '').toLowerCase(), r]));
    return getDirectReports(orgRows, String(managerEmail || '').toLowerCase()).map((r) => {
      const email = String(r.user_email || '').toLowerCase();
      const isManager = !!r.is_manager;
      const stub = stubs.get(email);
      const summary = stub?.own?.summary;
      return {
        email,
        isManager,
        required: Number(summary?.required_total) || 0,
        completed: Number(summary?.required_completed) || 0,
      };
    });
  }, [orgRows, managerEmail, eduNode]);

  if (!eduNode || !rows.length) return null;

  return (
    <StbAccordion variant="minimal" label="Required Education by Direct Report" isOpen={true} toggleIsOpen={() => {}}>
      <div style={{ padding: 12 }}>
        <div style={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto' }}>
          <div
            className="flex items-center hbr-css__text-secondary-sm"
            style={{ position: 'sticky', top: 0, zIndex: 1, background: '#fff', padding: '0 16px 6px', gap: 8 }}
            aria-hidden="true"
          >
            <div className="flex-1 min-w-0">Direct report</div>
            <div style={COUNT_COL}>Required</div>
            <div style={COUNT_COL}>Completed</div>
            <div style={{ width: 16 }} />
          </div>
          <div className="flex flex-col" style={{ gap: 8 }}>
            {rows.map((r) => (
              <StbAccordion
                key={r.email}
                variant="minimal"
                className="w-full min-w-0 bg-white border border-gray-400 rounded"
                isOpen={!!open[r.email]}
                toggleIsOpen={(o) => setOpen((prev) => ({ ...prev, [r.email]: o }))}
                headerContent={
                  <div className="flex items-center flex-1 min-w-0" style={{ gap: 8 }}>
                    <div className="flex-1 min-w-0 truncate font-semibold" title={r.email}>
                      {r.email}
                      {r.isManager ? ' (Mgr)' : ''}
                    </div>
                    <div style={COUNT_COL} title="Required series assigned">
                      {r.required}
                    </div>
                    <div style={COUNT_COL} title="Required series completed">
                      {r.completed}
                    </div>
                  </div>
                }
              >
                {open[r.email] && (
                  // The series table has fixed column widths; contain its inline size so it scrolls inside the
                  // card instead of widening the accordion's content-sized grid track past its container.
                  <div style={{ padding: 12, overflowX: 'auto', contain: 'inline-size' }}>
                    <ReportRequiredSeries email={r.email} timeFilter={timeFilter} />
                  </div>
                )}
              </StbAccordion>
            ))}
          </div>
        </div>
      </div>
    </StbAccordion>
  );
}
